import { useEffect, useState } from "react";
import { Phone, Mail, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "quoted", "booked", "lost"];
const statusColor = { new: "bg-[#D99B41]", contacted: "bg-[#4A403B]", quoted: "bg-[#B84F3E]", booked: "bg-[#477A5D]", lost: "bg-[#8A7970]" };

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");

  const load = () => {
    const params = filter ? { status: filter } : {};
    api.get("/leads", { params }).then(r => setLeads(r.data));
  };
  useEffect(load, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      toast.success("Updated");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-serif text-4xl">Leads</h1>
        <p className="text-[#8A7970] mt-1">All enquiries from Meta ads, WhatsApp, website & GMB.</p>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setFilter("")} data-testid="filter-all"
          className={`px-4 py-1.5 rounded-full text-sm border ${filter === "" ? "bg-[#B84F3E] text-white border-[#B84F3E]" : "bg-white border-[#E7C2A6]"}`}>All</button>
        {STATUSES.map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
            className={`px-4 py-1.5 rounded-full text-sm border capitalize ${filter === s ? "bg-[#B84F3E] text-white border-[#B84F3E]" : "bg-white border-[#E7C2A6]"}`}>{s}</button>
        ))}
      </div>

      <div className="bg-[#FDFBF7] rounded-xl border border-[#E7C2A6]/40 overflow-hidden">
        {leads.length === 0 ? (
          <div className="p-10 text-center text-[#8A7970]">No leads yet. Once Meta ads start flowing, they'll appear here.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F5F2EB] text-[#8A7970]">
              <tr>
                <th className="text-left p-3 font-medium">Guest</th>
                <th className="text-left p-3 font-medium">Contact</th>
                <th className="text-left p-3 font-medium">Dates</th>
                <th className="text-left p-3 font-medium">Package</th>
                <th className="text-left p-3 font-medium">Source</th>
                <th className="text-left p-3 font-medium">Status</th>
                <th className="text-left p-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(l => (
                <tr key={l.id} className="border-t border-[#E7C2A6]/30 hover:bg-[#F5F2EB]/50" data-testid={`lead-row-${l.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{l.name}</div>
                    <div className="text-xs text-[#8A7970]">{l.guests || "-"} guests</div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" strokeWidth={1.5}/>{l.phone}</div>
                    {l.email && <div className="flex items-center gap-1.5 text-xs text-[#8A7970]"><Mail className="w-3 h-3" strokeWidth={1.5}/>{l.email}</div>}
                  </td>
                  <td className="p-3 text-xs">{l.checkin ? `${l.checkin} → ${l.checkout}` : "—"}</td>
                  <td className="p-3 text-xs capitalize">{l.package_pref || "—"}</td>
                  <td className="p-3"><Badge variant="outline" className="text-xs capitalize">{l.source}</Badge></td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-white text-xs capitalize ${statusColor[l.status] || "bg-[#8A7970]"}`}>{l.status}</span>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <a href={`https://wa.me/${l.phone.replace(/[^0-9]/g,'')}?text=Hi ${l.name}, this is Megh Balika Homestay.`} target="_blank" rel="noreferrer" data-testid={`wa-lead-${l.id}`}>
                        <Button size="sm" variant="outline" className="h-8"><MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5}/></Button>
                      </a>
                      <select className="text-xs rounded-md border border-[#E7C2A6] bg-white px-2 h-8" value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} data-testid={`status-select-${l.id}`}>
                        {STATUSES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
